<script setup>
definePage({
  meta: {
    requiresAuth: true,
    role: ['superadmin'],
  },
})
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import { useSuperadmin } from '@/composables/useSuperadmin'
import axios from 'axios'

const { t } = useI18n()
const { showSnackbar } = useSnackbar()
const { isSuperadmin } = useSuperadmin()

const isLoading = ref(false)
const isSavingRates = ref(false)
const isSavingDefaultCurrency = ref(false)
const isSavingShelf = ref(false)

const usdToMga = ref(null)
const cnyToMga = ref(null)
const defaultInvoiceCurrency = ref('MGA')

// Mada warehouse shelf grid. Operator extends these when more racks
// are added. Existing parcels keep their assignments — narrowing the
// range doesn't auto-clear stale shelf codes.
const shelfGondoleMax = ref(7)
const shelfLigneMin   = ref(4)
const shelfLigneMax   = ref(8)

const shippingRates = ref([])
const isLoadingRates = ref(false)
const editingRow = ref(null)
const editForm = ref({})

const newRate = ref({
  name: '',
  transport_type: 'sea',
  calculation_type: 'volume',
  pricing_category: 'TRANSPORT',
  rate: 0,
  currency: 'USD',
  min_value: null,
  is_active: true,
})

const transportTypes = ['sea', 'air']
const calculationTypes = ['volume', 'weight', 'volumetric']
const currencies = ['USD', 'CNY', 'MGA']
const invoiceCurrencies = ['MGA', 'USD', 'CNY']

// APK is dropped at /public/downloads/godana-warehouse.apk on the server
// (not in git — see public/downloads/.gitignore). Cache-bust with the
// file's actual mtime fetched from /api/shipment/settings/apk-info so a
// fresh upload always produces a different URL — the previous date-only
// stamp gave the same value all day, so workers got stale APKs from the
// browser cache when ops swapped the file mid-day.
const apkMtime = ref(null)
const fetchApkInfo = async () => {
  try {
    const { data } = await axios.get('shipment/settings/apk-info')
    if (data.exists) apkMtime.value = data.mtime
  } catch (e) {
    console.error('Failed to fetch APK info:', e)
    // Fall back to today's date so the URL still works (just less fresh).
    apkMtime.value = Math.floor(Date.now() / 1000)
  }
}
const apkUrl = computed(() => {
  const base = `${window.location.origin}/downloads/godana-warehouse.apk`
  const stamp = apkMtime.value ?? Math.floor(Date.now() / 1000)
  return `${base}?v=${stamp}`
})
const apkQrUrl = computed(() =>
  `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(apkUrl.value)}`,
)

const fetchExchangeRates = async () => {
  isLoading.value = true
  try {
    const { data } = await axios.get('shipment/settings/exchange-rates')
    usdToMga.value = data.usd_to_mga
    cnyToMga.value = data.cny_to_mga
    defaultInvoiceCurrency.value = data.default_invoice_currency || 'MGA'
    if (data.shelf_gondole_max) shelfGondoleMax.value = data.shelf_gondole_max
    if (data.shelf_ligne_min)   shelfLigneMin.value   = data.shelf_ligne_min
    if (data.shelf_ligne_max)   shelfLigneMax.value   = data.shelf_ligne_max
  } catch (e) {
    console.error(e)
    showSnackbar({ color: 'error', text: t('messages.something_went_wrong') })
  } finally {
    isLoading.value = false
  }
}

const fetchShippingRates = async () => {
  isLoadingRates.value = true
  try {
    const { data } = await axios.get('shipment/shipping-rates', { params: { per_page: 100 } })
    shippingRates.value = data.data || data
  } catch (e) {
    console.error(e)
    showSnackbar({ color: 'error', text: t('messages.something_went_wrong') })
  } finally {
    isLoadingRates.value = false
  }
}

const saveExchangeRate = async (currency) => {
  isSavingRates.value = true
  try {
    const value = currency === 'usd' ? usdToMga.value : cnyToMga.value
    await axios.put('shipment/settings/exchange-rate', { value, currency })
    showSnackbar({ color: 'success', text: t('shipment.settings_saved') })
  } catch (e) {
    console.error(e)
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  } finally {
    isSavingRates.value = false
  }
}

const saveDefaultInvoiceCurrency = async () => {
  isSavingDefaultCurrency.value = true
  try {
    await axios.put('shipment/settings/default-invoice-currency', { currency: defaultInvoiceCurrency.value })
    showSnackbar({ color: 'success', text: t('shipment.settings_saved') })
  } catch (e) {
    console.error(e)
    showSnackbar({ color: 'error', text: t('messages.something_went_wrong') })
  } finally {
    isSavingDefaultCurrency.value = false
  }
}

// Save the shelf range update to /shipment/settings/shelf and
// invalidate the shared composable cache so any open dialog gets
// the new range on next render.
const saveShelf = async () => {
  isSavingShelf.value = true
  try {
    await axios.put('shipment/settings/shelf', {
      shelf_gondole_max: shelfGondoleMax.value,
      shelf_ligne_min:   shelfLigneMin.value,
      shelf_ligne_max:   shelfLigneMax.value,
    })
    showSnackbar({ color: 'success', text: t('shipment.settings_saved') })
    // Force the useShelfConfig composable to re-fetch so other open
    // dialogs (the user might already have one open in another tab)
    // pick up the new range. Defensive — most operators close the
    // settings tab before reopening a measure dialog anyway.
    try {
      const { useShelfConfig } = await import('@/composables/useShelfConfig')
      useShelfConfig().reload()
    } catch (_) { /* ignore */ }
  } catch (e) {
    console.error(e)
    const errs = e.response?.data?.errors
    showSnackbar({
      color: 'error',
      text: errs
        ? Object.values(errs).flat().join(', ')
        : (e.response?.data?.message || t('messages.something_went_wrong')),
    })
  } finally {
    isSavingShelf.value = false
  }
}

const startEdit = (rate) => {
  editingRow.value = rate.id
  editForm.value = { ...rate }
}

const cancelEdit = () => {
  editingRow.value = null
  editForm.value = {}
}

const saveEdit = async () => {
  try {
    await axios.put(`shipment/shipping-rates/${editForm.value.id}`, editForm.value)
    showSnackbar({ color: 'success', text: t('shipment.settings_saved') })
    editingRow.value = null
    await fetchShippingRates()
  } catch (e) {
    console.error(e)
    showSnackbar({ color: 'error', text: e.response?.data?.errors ? Object.values(e.response.data.errors).flat().join(', ') : t('messages.something_went_wrong') })
  }
}

const toggleActive = async (rate) => {
  try {
    await axios.put(`shipment/shipping-rates/${rate.id}`, { is_active: !rate.is_active })
    await fetchShippingRates()
  } catch (e) {
    console.error(e)
  }
}

const deleteRate = async (rate) => {
  if (!confirm(t('shipment.confirm_delete_rate'))) return
  try {
    await axios.delete(`shipment/shipping-rates/${rate.id}`)
    showSnackbar({ color: 'success', text: t('shipment.deleted') })
    await fetchShippingRates()
  } catch (e) {
    console.error(e)
    showSnackbar({ color: 'error', text: t('messages.something_went_wrong') })
  }
}

const addRate = async () => {
  try {
    await axios.post('shipment/shipping-rates', newRate.value)
    showSnackbar({ color: 'success', text: t('shipment.settings_saved') })
    newRate.value = {
      name: '',
      transport_type: 'sea',
      calculation_type: 'volume',
      pricing_category: 'TRANSPORT',
      rate: 0,
      currency: 'USD',
      min_value: null,
      is_active: true,
    }
    await fetchShippingRates()
  } catch (e) {
    console.error(e)
    const errs = e.response?.data?.errors
    showSnackbar({
      color: 'error',
      text: errs ? Object.values(errs).flat().join(', ') : t('messages.something_went_wrong'),
    })
  }
}

const seedDefaults = async () => {
  try {
    await axios.post('shipment/shipping-rates/seed-defaults')
    showSnackbar({ color: 'success', text: t('shipment.settings_saved') })
    await fetchShippingRates()
  } catch (e) {
    console.error(e)
    showSnackbar({ color: 'error', text: t('messages.something_went_wrong') })
  }
}

onMounted(() => {
  fetchExchangeRates()
  fetchShippingRates()
  fetchApkInfo()
})
</script>

<template>
  <div>
    <VCard class="mb-4">
      <VCardText>
        <h2 class="text-h4">{{ t('shipment.settings') }}</h2>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ t('shipment.settings_description') }}
        </p>
      </VCardText>
    </VCard>

    <!-- Warehouse Android app download -->
    <VCard class="mb-4">
      <VCardText>
        <VRow align="center">
          <VCol cols="12" md="8">
            <div class="d-flex align-center mb-2">
              <VIcon icon="tabler-brand-android" color="success" size="32" class="me-3" />
              <div>
                <h3 class="text-h5 mb-0">
                  {{ t('shipment.mobile_app.title') }}
                </h3>
                <p class="text-body-2 text-medium-emphasis mb-0">
                  {{ t('shipment.mobile_app.subtitle') }}
                </p>
              </div>
            </div>
            <p class="text-body-2 mt-3 mb-2">
              {{ t('shipment.mobile_app.description') }}
            </p>
            <p class="text-caption text-medium-emphasis mb-0">
              <VIcon icon="tabler-info-circle" size="14" /> {{ t('shipment.mobile_app.install_hint') }}
            </p>
          </VCol>
          <VCol cols="12" md="4" class="text-md-end text-center">
            <VBtn
              color="success"
              size="large"
              prepend-icon="tabler-download"
              :href="apkUrl"
              download
            >
              {{ t('shipment.mobile_app.download') }}
            </VBtn>
            <div class="mt-3">
              <img
                :src="apkQrUrl"
                :alt="t('shipment.mobile_app.scan_qr')"
                width="140"
                height="140"
                style="border: 1px solid #ddd; border-radius: 8px; padding: 4px;"
              >
              <div class="text-caption text-medium-emphasis mt-1">
                <VIcon icon="tabler-qrcode" size="14" /> {{ t('shipment.mobile_app.scan_qr') }}
              </div>
            </div>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <VRow>
      <!-- Exchange rates card -->
      <VCol cols="12" md="6">
        <VCard>
          <VCardTitle>{{ t('shipment.exchange_rates') }}</VCardTitle>
          <VCardSubtitle class="pb-3">{{ t('shipment.exchange_rates_help') }}</VCardSubtitle>
          <VDivider />
          <VCardText>
            <VProgressLinear v-if="isLoading" indeterminate />
            <template v-else>
              <div class="d-flex align-center ga-3 mb-4">
                <VTextField
                  v-model.number="usdToMga"
                  :label="`USD → MGA`"
                  type="number"
                  density="compact"
                  hide-details
                  style="max-width: 220px"
                />
                <VBtn
                  color="primary"
                  :loading="isSavingRates"
                  size="small"
                  @click="saveExchangeRate('usd')"
                >
                  {{ t('shipment.save') }}
                </VBtn>
              </div>
              <div class="d-flex align-center ga-3 mb-4">
                <VTextField
                  v-model.number="cnyToMga"
                  :label="`CNY → MGA`"
                  type="number"
                  density="compact"
                  hide-details
                  style="max-width: 220px"
                />
                <VBtn
                  color="primary"
                  :loading="isSavingRates"
                  size="small"
                  @click="saveExchangeRate('cny')"
                >
                  {{ t('shipment.save') }}
                </VBtn>
              </div>
            </template>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Default invoice currency -->
      <VCol cols="12" md="6">
        <VCard>
          <VCardTitle>{{ t('shipment.default_invoice_currency') }}</VCardTitle>
          <VCardSubtitle class="pb-3">{{ t('shipment.default_invoice_currency_help') }}</VCardSubtitle>
          <VDivider />
          <VCardText>
            <div class="d-flex align-center ga-3">
              <VSelect
                v-model="defaultInvoiceCurrency"
                :items="invoiceCurrencies"
                density="compact"
                hide-details
                style="max-width: 220px"
              />
              <VBtn
                color="primary"
                :loading="isSavingDefaultCurrency"
                size="small"
                @click="saveDefaultInvoiceCurrency"
              >
                {{ t('shipment.save') }}
              </VBtn>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Mada warehouse shelf grid editor. Defines the G* / L*
           ranges that the measurement dialogs show. Bumping the max
           values doesn't migrate existing parcels; narrowing the
           range doesn't auto-clear stale shelf codes either. -->
      <VCol cols="12" md="12">
        <VCard>
          <VCardTitle>{{ t('shipment.shelf_settings_title') }}</VCardTitle>
          <VCardSubtitle class="pb-3">{{ t('shipment.shelf_settings_help') }}</VCardSubtitle>
          <VDivider />
          <VCardText>
            <VRow dense>
              <VCol cols="12" md="3">
                <VTextField
                  v-model.number="shelfGondoleMax"
                  :label="t('shipment.shelf_gondole_max')"
                  type="number"
                  min="1"
                  max="50"
                  density="compact"
                  prepend-inner-icon="tabler-rectangle-vertical"
                  :hint="t('shipment.shelf_gondole_max_hint')"
                  persistent-hint
                />
              </VCol>
              <VCol cols="12" md="3">
                <VTextField
                  v-model.number="shelfLigneMin"
                  :label="t('shipment.shelf_ligne_min')"
                  type="number"
                  min="1"
                  max="99"
                  density="compact"
                  prepend-inner-icon="tabler-layout-rows"
                />
              </VCol>
              <VCol cols="12" md="3">
                <VTextField
                  v-model.number="shelfLigneMax"
                  :label="t('shipment.shelf_ligne_max')"
                  type="number"
                  min="1"
                  max="99"
                  density="compact"
                  prepend-inner-icon="tabler-layout-rows"
                />
              </VCol>
              <VCol cols="12" md="3" class="d-flex align-center">
                <VBtn
                  color="primary"
                  :loading="isSavingShelf"
                  block
                  @click="saveShelf"
                >
                  {{ t('shipment.save') }}
                </VBtn>
              </VCol>
            </VRow>
            <VAlert
              v-if="shelfLigneMin > shelfLigneMax"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              {{ t('shipment.shelf_invalid_range') }}
            </VAlert>
            <p class="text-caption text-medium-emphasis mt-3 mb-0">
              {{ t('shelf_current_preview', 'Current grid:') }}
              G1 … G{{ shelfGondoleMax }} × L{{ shelfLigneMin }} … L{{ shelfLigneMax }}
              ({{ shelfGondoleMax * Math.max(0, (shelfLigneMax - shelfLigneMin + 1)) }} {{ t('shipment.shelf_slots_count', 'slots') }})
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Shipping rates table -->
    <VCard class="mt-4">
      <VCardTitle class="d-flex align-center justify-space-between">
        <span>{{ t('shipment.shipping_rates_table') }}</span>
        <VBtn size="small" variant="tonal" @click="seedDefaults">
          {{ t('shipment.seed_defaults') }}
        </VBtn>
      </VCardTitle>
      <VDivider />
      <VCardText>
        <VProgressLinear v-if="isLoadingRates" indeterminate />
        <VTable v-else density="compact">
          <thead>
            <tr>
              <th>{{ t('shipment.name') }}</th>
              <th>{{ t('shipment.transport') }}</th>
              <th>{{ t('shipment.calculation_type') }}</th>
              <th>{{ t('shipment.pricing_category') }}</th>
              <th class="text-right">{{ t('shipment.rate') }}</th>
              <th>{{ t('shipment.currency') }}</th>
              <th>{{ t('shipment.min_value') }}</th>
              <th>{{ t('general.active') }}</th>
              <th class="text-right">{{ t('shipment.action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rate in shippingRates" :key="rate.id">
              <template v-if="editingRow === rate.id">
                <td><VTextField v-model="editForm.name" density="compact" hide-details /></td>
                <td><VSelect v-model="editForm.transport_type" :items="transportTypes" density="compact" hide-details /></td>
                <td><VSelect v-model="editForm.calculation_type" :items="calculationTypes" density="compact" hide-details /></td>
                <td><VTextField v-model="editForm.pricing_category" density="compact" hide-details /></td>
                <td><VTextField v-model.number="editForm.rate" type="number" density="compact" hide-details class="text-right" /></td>
                <td><VSelect v-model="editForm.currency" :items="currencies" density="compact" hide-details /></td>
                <td><VTextField v-model.number="editForm.min_value" type="number" density="compact" hide-details /></td>
                <td>
                  <VSwitch v-model="editForm.is_active" hide-details density="compact" color="primary" />
                </td>
                <td class="text-right">
                  <VBtn size="x-small" color="success" variant="text" icon @click="saveEdit">
                    <VIcon icon="tabler-check" />
                  </VBtn>
                  <VBtn size="x-small" variant="text" icon @click="cancelEdit">
                    <VIcon icon="tabler-x" />
                  </VBtn>
                </td>
              </template>
              <template v-else>
                <td>{{ rate.name }}</td>
                <td>{{ rate.transport_type }}</td>
                <td>{{ rate.calculation_type }}</td>
                <td>{{ rate.pricing_category }}</td>
                <td class="text-right">{{ Number(rate.rate).toLocaleString() }}</td>
                <td>{{ rate.currency }}</td>
                <td>{{ rate.min_value ?? '—' }}</td>
                <td>
                  <VSwitch
                    :model-value="rate.is_active"
                    hide-details
                    density="compact"
                    color="primary"
                    @update:model-value="toggleActive(rate)"
                  />
                </td>
                <td class="text-right">
                  <VBtn size="x-small" color="primary" variant="text" icon @click="startEdit(rate)">
                    <VIcon icon="tabler-edit" />
                  </VBtn>
                  <VBtn
                    v-tooltip="!isSuperadmin ? t('shipment.superadmin_only') : ''"
                    size="x-small"
                    color="error"
                    variant="text"
                    icon
                    :disabled="!isSuperadmin"
                    @click="deleteRate(rate)"
                  >
                    <VIcon icon="tabler-trash" />
                  </VBtn>
                </td>
              </template>
            </tr>
            <!-- Add row -->
            <tr>
              <td><VTextField v-model="newRate.name" density="compact" hide-details :placeholder="t('shipment.name')" /></td>
              <td><VSelect v-model="newRate.transport_type" :items="transportTypes" density="compact" hide-details /></td>
              <td><VSelect v-model="newRate.calculation_type" :items="calculationTypes" density="compact" hide-details /></td>
              <td><VTextField v-model="newRate.pricing_category" density="compact" hide-details /></td>
              <td><VTextField v-model.number="newRate.rate" type="number" density="compact" hide-details /></td>
              <td><VSelect v-model="newRate.currency" :items="currencies" density="compact" hide-details /></td>
              <td><VTextField v-model.number="newRate.min_value" type="number" density="compact" hide-details /></td>
              <td>
                <VSwitch v-model="newRate.is_active" hide-details density="compact" color="primary" />
              </td>
              <td class="text-right">
                <VBtn size="small" color="primary" variant="tonal" prepend-icon="tabler-plus" @click="addRate">
                  {{ t('general.add') }}
                </VBtn>
              </td>
            </tr>
          </tbody>
        </VTable>
      </VCardText>
    </VCard>
  </div>
</template>
